import React, { FC, useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useContext } from 'react';
import { StateContext, DispatchContext } from '../../AppContext'
import { useFilters, usePagination, useSortBy, useTable, useGlobalFilter } from 'react-table';
import Header from 'src/components/header/Header';
import { IFilterValues, IState, IVouchers } from 'src/Interface';
import envelopeIcon from 'src/assets/images/group-11.png'
import checkMarkIcon from 'src/assets/images/group-11-copy.svg'
import styles from './vouchers.module.scss'
import { differenceInMinutes, format, sub, differenceInDays, isBefore, isAfter, parseISO, isSameDay, previousSaturday } from 'date-fns';
import Button from 'src/components/button/Button';
import DatePicker from 'react-datepicker'
import Input from 'src/components/input/Input';
import Checkbox from 'src/components/checkbox/Checkbox';
import axios from 'axios';
import { baseApiURL } from 'src/utils/utils';
import SubHeader from 'src/components/subHeader/SubHeader'
import { toast } from 'react-toastify';
import SearchField from 'src/components/searchField/SearchField';
import Footer from 'src/components/footer/Footer';
import DropDown from 'src/components/dropDown/Dropdown';
import Table from 'src/components/table/Table';
import useCancellablePromise from 'src/utils/useCancellablePromise';
import isSunday from 'date-fns/isSunday';

const Vouchers: FC = () => {
  const [showFilters, toggleFilter] = useState(false)
  const [fromDate, setFromDate] = useState(sub(new Date(), { days: 30 }))
  const [toDate, setToDate] = useState(new Date())
  const [currentFromDate, setCurrentFromDate] = useState(null)
  const [currentToDate, setCurrentToDate] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [fromExpiryDate, setFromExpiryDate] = useState(null)
  const [toExpiryDate, setToExpiryDate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const [companies, setCompanies] = useState([])
  const isInitialMount = useRef(true)
  const [branches, setBranches] = useState([])
  const { cancellablePromise } = useCancellablePromise()
  const [companyId, setCompanyId] = useState(undefined)
  const [branchId, setBranchId] = useState(undefined)
  const dispatch: React.Dispatch<any> = useContext(DispatchContext)
  const [filters, setFilters] = useState<IFilterValues>({
    customerNames: [],
    references: [],
    statuses: [],
    issuedByEmployees: [],
    toExpiryDate,
    fromExpiryDate
  })
  const [vouchers, setVouchers] = useState<Array<IVouchers>>([])
  const state: IState = useContext(StateContext)
  const data = useMemo(() => vouchers, [vouchers])
  const statuses = useMemo(() => {
    return {
      REDEEMED: {
        label: 'Indløst',
        style: 'status--white'
      },
      LOCKED: {
        label: 'I brug',
        style: 'status--blue'
      },
      ACTIVE: {
        label: 'Aktiv',
        style: 'status--green'
      },
      EXPIRED: {
        label: 'Udløbet',
        style: 'status--red'
      },
      CANCELLED: {
        label: 'Udløbet',
        style: 'status--red'
      }
    }
  }, [])

  const columns = useMemo(() => {
    return [
      {
        Header: 'Oprettet',
        accessor: 'issuedDate',
        Cell: (item: any) => {
          return <span>{format(new Date(item.value), 'dd/MM/yyyy')}</span>
        }
      },
      {
        Header: 'Udstedt af',
        show: false,
        accessor: 'issuedBy',
        filter: multiSelectFilter
      },
      {
        Header: 'Modtager',
        accessor: 'voucherCustomerName',
        filter: multiSelectFilter
      },
      {
        Header: 'Reference',
        accessor: 'reference',
        filter: multiSelectFilter
      },
      {
        Header: 'Udløb',
        accessor: 'expiryDate',
        filter: filterDates,
        Cell: (item: any) => <span>{getDifference(item.value)}</span>
      },
      {
        Header: 'Status',
        accessor: 'status',
        filter: multiSelectFilter,
        Cell: (item: any) => <span className={`status ${statuses[item.value]?.style}`}>{statuses[item.value]?.label}</span>
      }
    ]
  }, [statuses])

  function multiSelectFilter(rows, columnIds, filterValue) {
    return filterValue.length === 0
      ? rows
      : rows.filter((row) =>
        filterValue.includes(String(row.original[columnIds])),
      );
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    pageOptions,
    nextPage,
    filteredRows,
    setAllFilters,
    gotoPage,
    canNextPage,
    canPreviousPage,
    setGlobalFilter,
    state: pageState,
    previousPage,
    setFilter,
    prepareRow,
  } = useTable({
    columns,
    data,
    autoResetPage: false
  }, useFilters, useGlobalFilter, useSortBy, usePagination)

  const [selectedFilters, updateSelectedFilters] = useState({
    customerNames: [],
    issuedByEmployees: [],
    references: [],
    statuses: []
  })

  useEffect(() => {
    if (state.user.accessToken && state.user.role === 'DanTaxiAdmin') {
      cancellablePromise(axios(`${baseApiURL()}/companies/lookup`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then((result) => {
        setCompanyId(result.data.find(company => company.id === Number(window.localStorage.getItem('companyId'))) ? result.data.find(company => company.id === Number(window.localStorage.getItem('companyId'))).id : result.data[0].id)
        setCompanies(result.data.map(company => {
          return { label: company.name, value: company.id }
        }).sort(function (a, b) {
          if (a.label < b.label) { return -1; }
          if (a.label > b.label) { return 1; }
          return 0;
        }))
      }).catch((err) => {
        toast(err.response.data.message)
      }))
    } else {
      setCompanyId(state.user.companyId)
      setCompanies([])
    }
  }, [state.user.accessToken, dispatch, state.user.role, state.user.companyId, cancellablePromise])

  const fetchBranches = useCallback((branchId?: number) => {
    cancellablePromise(axios(`${baseApiURL()}/companies/${companyId}/branches/${branchId ? branchId : ''}`, {
      headers: {
        Authorization:
          `Bearer ${state.user.accessToken}`
      }
    }).then((result) => {
      if (branchId) {
        setBranches([{ label: result.data.name, value: branchId, vouchersAccount: result.data.vouchersAccount, users: result.data.users }])
        dispatch({ type: 'SET_BRANCHES', branches: [result.data] })
        setBranchId(branchId)
      } else {
        dispatch({ type: 'SET_BRANCHES', branches: result.data })
        setBranches(result.data.map(branch => {
          return { label: branch.name, value: branch.id, vouchersAccount: branch.vouchersAccount, users: branch.users }
        }))
        setBranchId(result.data.find(branch => branch.id === Number(window.localStorage.getItem('branchId'))) ? result.data.find(branch => branch.id === Number(window.localStorage.getItem('branchId'))).id : result.data[0].id)
      }
    }).catch((err) => {
      setLoading(false)
      toast(err.response?.data.message)
    }))
  }, [cancellablePromise, companyId, dispatch, state.user.accessToken])

  const fetchVouchers = useCallback(() => {
    // Use tableLoading for subsequent loads, loading only for initial mount
    if (isInitialMount.current) {
      setLoading(true)
      isInitialMount.current = false
    } else {
      setTableLoading(true)
    }
    if (branchId && state.user.accessToken) {
      cancellablePromise(axios(`${baseApiURL()}/branches/${branchId}/vouchers?fromDate=${(currentFromDate ? currentFromDate : sub(new Date(), { days: 30 })).toISOString()}&toDate=${currentToDate ? currentToDate.toISOString() : ''}`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then((result) => {
        setFilters(result.data.filters)
        setVouchers(result.data.vouchers)
        updateSelectedFilters({
          customerNames: [],
          issuedByEmployees: [],
          references: [],
          statuses: []
        })
        dispatch({ type: 'SET_VOUCHERS', vouchers: result.data.vouchers, filters: result.data.filters })
        setLoading(false)
        setTableLoading(false)
      }).catch((err) => {
        setLoading(false)
        setTableLoading(false)
        toast(err.response.data.message)
      }))
    }
  }, [state.user.accessToken, branchId, dispatch, currentFromDate, currentToDate, cancellablePromise])

  useEffect(() => {
    fetchVouchers()
  }, [fetchVouchers])


  useEffect(() => {
    if (state.user.accessToken && companyId) {
      if (state.user.role !== 'BranchAdmin') {
        fetchBranches()
      }
      else {
        fetchBranches(state.user.branchId)
      }
    }
  }, [companyId, fetchBranches, state.user.accessToken, state.user.branchId, state.user.role])


  const filterSearch = (category: string) => (value: string) => {
    const filtersCopy = { ...filters }
    filtersCopy[category] = state.voucherFilters[category].filter((values) => {
      return values.name.toLowerCase().includes(value.toLowerCase())
    })

    setFilters(filtersCopy)
  }

  function filterDates(rows: any, id: any, filterValue: Array<string>) {
    const [fromExpireDate, toExpireDate]: any = filterValue
    return filterValue.length === 0 ? rows :
      rows.filter((row: any) => {
        return (
          (isAfter(parseISO(row.original.expiryDate), fromExpireDate) || isSameDay(parseISO(row.original.expiryDate), fromExpireDate))
          &&
          (isBefore(parseISO(row.original.expiryDate), toExpireDate)) || isSameDay(parseISO(row.original.expiryDate), toExpireDate))
      })
  }


  const updateList = (name: string, category: string) => (checked: boolean) => {
    const filterCategories: Record<string, string> = {
      customerNames: 'voucherCustomerName',
      issuedByEmployees: 'issuedBy',
      references: 'reference',
      statuses: 'status'
    }
    const filterCategory = filterCategories[category]
    if (checked) {
      updateSelectedFilters({
        ...selectedFilters,
        [category]: [...selectedFilters[category], name]
      })

      setFilter(filterCategory, [...selectedFilters[category], name])
      gotoPage(0)
    } else {
      const selectedFilterOptionsCopy = { ...selectedFilters }
      selectedFilterOptionsCopy[category].splice(selectedFilters[category].findIndex((n: any) => n === name), 1)
      updateSelectedFilters(selectedFilterOptionsCopy)
      setFilter(filterCategory, selectedFilterOptionsCopy[category])
      gotoPage(0)
    }
  }

  const getDifference = (date: string) => {
    let days = differenceInDays(new Date(date), new Date()) // should use current date provided from API instead 
    if (days > 0) {
      const years = Math.floor(days / 365);
      days = days % 365;
      const months = Math.floor(days / 30);
      days = days % 30

      return `${years ? `${years} år` : ''}${years && months && days ? ',' : years && months && !days ? ' og' : ''} ${months ? `${months} måned` : ''} ${(years || months) && days ? 'og' : ''} ${days > 1 ? `${days} dage` : days ? `${days} dag` : ''}`

    } else {
      let minutes = differenceInMinutes(new Date(date), new Date())
      const hours = Math.floor(minutes / 60)
      minutes = minutes % 60
      if (hours > 0) {
        return `${hours} timer`
      } else if (minutes > 0) {
        return `${minutes} minuter`
      } else {
        return 'Udløbet'
      }
    }
  }

  const dateFilterButtons = useMemo(() => [
    {
      label: 'Sidste uge',
      modifiers: 'tw-mb-3 tw-mr-auto',
      date: sub(new Date(), { weeks: 1 }),
    },
    {
      label: 'Sidste weekend',
      modifiers: 'tw-mb-3',
      date: isSunday(new Date()) ? sub(new Date(), { days: 8 }) : previousSaturday(new Date()),
    },
    {
      label: 'Sidste 30 dage',
      modifiers: 'tw-mr-auto',
      date: sub(new Date(), { days: 10 }),
    },
    {
      label: 'Sidste år',
      modifiers: '',
      date: sub(new Date(), { years: 1 })
    }
  ], [])

  const combinedSelectedFilters = [
    ...selectedFilters.customerNames.map((customerNames: any) => {
      return {
        name: customerNames,
        label: customerNames,
        category: 'customerNames'
      }
    }),
    ...selectedFilters.issuedByEmployees.map((employee: string) => {
      return {
        name: employee,
        label: employee,
        category: 'issuedByEmployees'
      }
    }),
    ...selectedFilters.references.map((branch: string) => {
      return {
        name: branch,
        label: branch,
        category: 'references'
      }
    }),
    ...selectedFilters.statuses.map((status: any) => {
      return {
        name: status,
        label: statuses[status].label,
        category: 'statuses'
      }
    })
  ]


  const branch = useMemo(() => branches.find(branch => branch.value === branchId), [branches, branchId])
  //Show for users where voucher have not been enabled on the branch
  if (branch && !branch.vouchersAccount) {
    return (
      <>
        <Header />
        <div className='tw-flex tw-flex-auto'>
          <aside className={styles.filterContainer}>
            <header className={styles.filterHeader}>
              <span className={styles.filterIcon}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                </svg>
              </span>
            </header>
          </aside>
          <div className={styles.container}>
            <SubHeader loading={false} title='Taxibon'>
              {companies.length > 0 && <DropDown options={companies} handleOnChange={(id) => {
                if (companyId !== id) {
                  setCompanyId(id)
                  setTableLoading(true)
                }
                window.localStorage.setItem('companyId', id)
              }} value={companyId} />}
              {branches.length > 0 && <DropDown modifiers='tw-ml-5' options={branches} handleOnChange={(id) => {
                if (branchId !== id) {
                  setBranchId(id)
                  setTableLoading(true)
                }
                window.localStorage.setItem('branchId', id)
              }} value={branchId} />}
              <SearchField disabled={true} modifiers='tw-ml-5' handleInputChange={(value) => setGlobalFilter(value)} name='search' label='Søg' />
              <Button disabled={true} label='Opret taxibon' modifiers='tw-ml-5' type='primary' iconLeft={<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>} />
            </SubHeader>

            <main className={styles.content}>
              <div className={styles.contactUs}>
                {state.user.role === 'DanTaxiAdmin' ?
                  <>
                    <img src={checkMarkIcon} alt="" />
                    <p className={styles.bodyText}>Aktiver Taxibon for denne kunde.</p>
                    <Button type='primary' label='Aktiver' modifiers='tw-w-full tw-justify-center' handleClick={() => dispatch({ type: 'SHOW_ASIDE', asideType: 'ToggleVouchers', show: true, branchId, size: 'small', companyId, fetchVouchers, fetchBranches })} />
                  </>
                  :
                  <>
                    <img src={envelopeIcon} alt="" />
                    <p className={styles.bodyText}>Kontakt din Dantaxi administrator for at aktivere Taxibonner</p>
                    <Button type='primary' label='Kontakt' modifiers='tw-w-full tw-justify-center' href='mailto:erhverv@dantaxi.dk' />
                  </>}
              </div>
            </main>
          </div>
        </div>
      </>
    )
  }

  return (
    <>

      <Header />
      <div className='tw-flex tw-flex-auto'>
        <aside className={`${styles.filterContainer} ${showFilters ? styles.animate : ''}`}>
          <header className={styles.filterHeader}>
            {showFilters && <h4 className={styles.subtitle}>Filtre</h4>}
            {!loading && <span className={styles.filterIcon} onClick={() => toggleFilter(!showFilters)}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
              </svg>
            </span>}
          </header>
          {showFilters && <div className={styles.filterContent}>
            <div className={styles.filterItem}>
              <input type="checkbox" id="date" className={styles.input} />
              <label htmlFor='date' className={styles.filterItemHeader}>
                <span className='tw-font-medium tw-text-sm tw-mr-auto '>Dato</span>
                {currentToDate && currentFromDate && <span className='tw-mr-3 tw-opacity-50 tw-text-xs self-center tw-flex'>
                  {format(currentFromDate, 'd/MM/yyyy')}
                  <svg className='tw-w-4 tw-h-4 tw-ml-2 tw-mr-2' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  {format(currentToDate, 'd/MM/yyyy')}</span>}
                <svg className={styles.filterItemIcon} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </label>
              <div className={styles.filterItemContent}>
                <div className='tw-flex tw-items-center tw-mb-5 tw-flex-wrap'>
                  {dateFilterButtons.map((button, index) =>
                    <Button key={index} modifiers='tw-mr-2' type={selectedDate === button.label ? 'addonActive' : 'addon'} handleClick={() => {
                      setFromDate(button.label === selectedDate ? sub(new Date(), { days: 10 }) : button.date)
                      setSelectedDate(button.label === selectedDate ? fromDate : button.label)
                    }}
                      label={button.label} />)}
                </div>
                <div className='tw-flex tw-items-center tw-mb-5'>
                  <DatePicker
                    selected={fromDate}
                    onChange={(date: any) => { setFromDate(date); setSelectedDate('') }}
                    selectsStart
                    maxDate={toDate}
                    dateFormat='dd/MM/yyyy'
                    className={styles.datePicker}
                    startDate={fromDate}
                  />
                  <span className={styles.arrowIcon}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <DatePicker
                    selected={selectedDate ? new Date() : toDate}
                    dateFormat='dd/MM/yyyy'
                    onChange={(date: any) => { setToDate(date); setSelectedDate('') }}
                    selectsEnd
                    minDate={fromDate}
                    maxDate={new Date()}
                    className={styles.datePicker}
                    startDate={toDate}
                  />
                </div>
                <Button type='primary' label='Apply' handleClick={() => {
                  setCurrentFromDate(fromDate)
                  setCurrentToDate(toDate)
                }} modifiers='mt-5 tw-ml-auto tw-w-full tw-justify-center' />
              </div>
            </div>
            <div className={styles.filterItem}>
              <input type="checkbox" id="createdByUser" className={styles.input} />
              <label htmlFor='createdByUser' className={styles.filterItemHeader}>
                <span className='tw-font-medium tw-text-sm tw-mr-auto'>Udstedet af</span>
                {selectedFilters.issuedByEmployees.length > 0 && <span className='tw-mr-3 tw-opacity-50 tw-text-xs self-center'>{`${selectedFilters.issuedByEmployees.length} ${selectedFilters.issuedByEmployees.length === 1 ? 'valgt' : 'valgte'}`}</span>}
                <svg className={styles.filterItemIcon} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </label>
              <div className={styles.filterItemContent}>
                <Input modifiers='tw-mb-2' searchIcon={true} placeholder='Søg' name='date' type='text' handleInputChange={() => filterSearch('issuedByEmployees')} />
                <ul>
                  {filters.issuedByEmployees.map(({ name, occurrences }, i) => (
                    <li className='tw-flex tw-items-center' key={i}>
                      <Checkbox modifiers='tw-py-3' checked={selectedFilters.issuedByEmployees.includes(name)} name={name} labelRight={name} handleChange={() => updateList(name, 'issuedByEmployees')} />
                      <span className='tw-ml-auto tw-text-sm tw-opacity-50'>{occurrences}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className={styles.filterItem}>
              <input type="checkbox" id="customerNames" className={styles.input} />
              <label htmlFor='customerNames' className={styles.filterItemHeader}>
                <span className='tw-font-medium tw-text-sm tw-mr-auto'>Modtager</span>
                {selectedFilters.customerNames.length > 0 && <span className='tw-mr-3 tw-opacity-50 tw-text-xs self-center'>{`${selectedFilters.customerNames.length} ${selectedFilters.customerNames.length === 1 ? 'valgt' : 'valgte'}`}</span>}
                <svg className={styles.filterItemIcon} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </label>
              <div className={styles.filterItemContent}>
                <Input modifiers='tw-mb-2' searchIcon={true} placeholder='Søg' name='date' type='text' handleInputChange={() => filterSearch('customerNames')} />
                <ul>
                  {filters.customerNames.map(({ name, occurrences }, i) => {
                    return (
                      <li className='tw-flex tw-items-center' key={i}>
                        <Checkbox modifiers='tw-py-3' checked={selectedFilters.customerNames.includes(name)} name={name} labelRight={name} handleChange={() => updateList(name, 'customerNames')} />
                        <span className='tw-ml-auto tw-text-sm tw-opacity-50'>{occurrences}</span>
                      </li>
                    )
                  }
                  )}
                </ul>
              </div>
            </div>
            <div className={styles.filterItem}>
              <input type="checkbox" id="reference" className={styles.input} />
              <label htmlFor='reference' className={styles.filterItemHeader}>
                <span className='tw-font-medium tw-text-sm tw-mr-auto'>Reference</span>
                {selectedFilters.references.length > 0 && <span className='tw-mr-3 tw-opacity-50 tw-text-xs self-center'>{`${selectedFilters.references.length} ${selectedFilters.references.length === 1 ? 'valgt' : 'valgte'}`}</span>}
                <svg className={styles.filterItemIcon} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </label>
              <div className={styles.filterItemContent}>
                <Input modifiers='tw-mb-2' searchIcon={true} placeholder='Søg' name='date' type='text' handleInputChange={() => filterSearch('references')} />
                <ul>
                  {filters.references.map(({ name, occurrences }, i) => (
                    <li className='tw-flex tw-items-center' key={i}>
                      <Checkbox modifiers='tw-py-3' checked={selectedFilters.references.includes(name)} name={name} labelRight={name} handleChange={() => updateList(name, 'references')} />
                      <span className='tw-ml-auto tw-text-sm tw-opacity-50'>{occurrences}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className={styles.filterItem}>
              <input type="checkbox" id="expiringAt" className={styles.input} />
              <label htmlFor='expiringAt' className={styles.filterItemHeader}>
                <span className='tw-font-medium tw-text-sm tw-mr-auto'>Udløb</span>
                <svg className={styles.filterItemIcon} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </label>
              <div className={styles.filterItemContent}>
                <div className="tw-flex justify-between">
                  <div className='tw-flex tw-flex-col'>
                    <label className='label' htmlFor="fra">Fra</label>
                    <DatePicker
                      id='fra'
                      selected={fromExpiryDate ? fromExpiryDate : new Date(filters.fromExpiryDate)}
                      dateFormat='dd/MM/yyyy'
                      onChange={(date: any) => {
                        setFromExpiryDate(date)
                        gotoPage(0)
                        setFilter('expiryDate', [date, toExpiryDate ? toExpiryDate : new Date(filters.toExpiryDate)])
                      }}
                      selectsEnd
                      minDate={new Date(filters.fromExpiryDate)}
                      maxDate={toExpiryDate || new Date(filters.toExpiryDate)}
                      className={styles.datePicker}
                    />
                  </div>
                  <span className={`${styles.arrowIcon} tw-mt-auto tw-mb-3`}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <div className='tw-flex tw-flex-col'>
                    <label className='label' htmlFor="til">Til</label>
                    <DatePicker
                      id='til'
                      selected={toExpiryDate ? toExpiryDate : new Date(filters.toExpiryDate)}
                      dateFormat='dd/MM/yyyy'
                      onChange={(date: any) => {
                        setToExpiryDate(date)
                        gotoPage(0)
                        setFilter('expiryDate', [fromExpiryDate ? fromExpiryDate : new Date(filters.fromExpiryDate), date])
                      }}
                      selectsEnd
                      minDate={fromExpiryDate || new Date(filters.fromExpiryDate)}
                      maxDate={new Date(filters.toExpiryDate)}
                      className={styles.datePicker}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.filterItem}>
              <input type="checkbox" id="status" className={styles.input} />
              <label htmlFor='status' className={styles.filterItemHeader}>
                <span className='tw-font-medium tw-text-sm tw-mr-auto'>Status</span>
                {selectedFilters.statuses.length > 0 && <span className='tw-mr-3 tw-opacity-50 tw-text-xs self-center'>{`${selectedFilters.statuses.length} ${selectedFilters.statuses.length === 1 ? 'valgt' : 'valgte'}`}</span>}
                <svg className={styles.filterItemIcon} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </label>
              <div className={styles.filterItemContent}>
                <Input modifiers='tw-mb-2' searchIcon={true} placeholder='Søg' name='date' type='text' handleInputChange={() => filterSearch('status')} />
                <ul>
                  {filters.statuses.map(({ name, occurrences }: any, i) => (
                    <li className='tw-flex tw-items-center' key={i}>
                      <Checkbox modifiers='tw-py-3' checked={selectedFilters.statuses.includes(name)} name={name} labelRight={statuses[name].label} handleChange={() => updateList(name, 'statuses')} />
                      <span className='tw-ml-auto tw-text-sm tw-opacity-50'>{occurrences}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>}
        </aside>
        <div className={styles.container}>
          <SubHeader loading={false} title={
            <>
              {format(currentFromDate ? currentFromDate : sub(new Date(), { days: 30 }), 'dd. MMM, yyyy')}
              <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-6 tw-w-6 tw-mx-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg> {format(currentToDate ? currentToDate : new Date(), 'dd. MMM, yyyy')}
            </>
          }>
            {companies.length > 0 && <DropDown options={companies} handleOnChange={(id) => {
              if (id !== companyId) {
                setCompanyId(id);
                window.localStorage.setItem('companyId', id)
                toggleFilter(false)
                setAllFilters([])
                updateSelectedFilters({
                  customerNames: [],
                  issuedByEmployees: [],
                  references: [],
                  statuses: []
                })
                setTableLoading(true)
              }
            }} value={companyId} />}
            {branches.length > 0 && <DropDown modifiers='tw-ml-5' options={branches} handleOnChange={(id) => {
              if (id !== branchId) {
                setBranchId(id)
                toggleFilter(false)
                setTableLoading(true)
                window.localStorage.setItem('branchId', id)
                setAllFilters([])
                updateSelectedFilters({
                  customerNames: [],
                  issuedByEmployees: [],
                  references: [],
                  statuses: []
                })
              }
            }} value={branchId} />}
            <SearchField modifiers='tw-ml-5' handleInputChange={(value) => setGlobalFilter(value)} name='search' label='Søg' />
            <Button handleClick={() => dispatch({ showModal: true, type: 'SHOW_MODAL', modalType: 'addVoucher', branchId, branch: branches.find(branch => branch.value === branchId), fetchVouchers })}
              label='Opret taxibon' modifiers='tw-ml-5' type='primary' iconLeft={<svg xmlns="http://www.w3.org/2000/svg" className="tw-h-4 tw-w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>} />
          </SubHeader>
          <main className={styles.content}>
            <div className='tw-flex tw-flex-wrap'>
              {combinedSelectedFilters.map((item, i) => {
                return (
                  <button key={i} onClick={() => updateList(item.name, item.category)(false)} className={`${styles.selectedFilterButton} tw-mr-3 tw-mb-5`}>
                    <span className={styles.selectedFilterButtonIcon}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </span>
                    {item.label}
                  </button>
                )
              })}
              {combinedSelectedFilters.length > 0 &&
                <>
                  <span className={styles.filterDivider} />
                  <button className={styles.clearFilters} onClick={() => {
                    setAllFilters([])
                    updateSelectedFilters({
                      customerNames: [],
                      issuedByEmployees: [],
                      references: [],
                      statuses: []
                    })
                  }}>Nulstil</button>
                </>}
            </div>
            <Table
              getTableBodyProps={getTableBodyProps}
              getTableProps={getTableProps}
              prepareRow={prepareRow}
              page={page}
              handleClick={(voucherId) => dispatch({ type: 'SHOW_ASIDE', show: true, asideType: 'VouchersDetails', voucherId, branchId, fetchVouchers })}
              loading={tableLoading}
              branchId={branchId}
              headerGroups={headerGroups}
              type='vouchers'
            />
          </main>
        </div>
        <Footer
          pageState={pageState}
          canNextPage={canNextPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          loading={false}
          page={page}
          paginationLabel='taxiboner'
          gotoPage={gotoPage}
          previousPage={previousPage}
          pageOptions={pageOptions}
          itemsLength={filteredRows.length}
        />
      </div>
    </>
  )
}

export default Vouchers